import {
  ArrowRightOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  UploadOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { Button, message, Modal, Space, Table, Upload } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SummaryLayout from '../../components/layout/SummaryLayout';
import MonthSelector from '../../components/ui/MonthSelector';
import { getData, getLocationOrigin } from '../../config/api-connect';
import { useMonthFilter } from '../../hooks/useMonthFilter';
import './style.css';

function BankSummary() {
  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [summaryLoader, setSummaryLoader] = useState(false);
  const [summaryData, setSummaryData] = useState([]);

  const get_bank_summary = useCallback(() => {
    setSummaryLoader(true);

    var month = selectedMonth.format('MM');
    var year = selectedMonth.format('YYYY');

    getData(`/finance/get_bank_payments_summary/?month=${month}&year=${year}`)
      .then((response) => {
        setSummaryData(response.result);
        setSummaryLoader(false);
      })
      .catch((error) => {
        setSummaryLoader(false);
        message.error(error.message || 'Failed to fetch summary');
      });
  }, [selectedMonth]);

  useEffect(() => {
    get_bank_summary();
  }, [get_bank_summary, selectedMonth]);

  const summaryTotals = useMemo(() => {
    if (!summaryData?.length) {
      return {
        bank_amount: 0,
        system_collection: 0,
        difference: 0,
      };
    }

    return summaryData.reduce(
      (acc, r) => {
        acc.bank_amount += r.bank_amount || 0;
        acc.system_collection += r.collection_amount || 0;
        acc.difference += r.difference || 0;
        return acc;
      },
      {
        bank_amount: 0,
        system_collection: 0,
        difference: 0,
      },
    );
  }, [summaryData]);

  const formatNumber = (value) => Math.round(value).toLocaleString('en-IN');

  const statsConfig = [
    {
      key: 'bank_amount',
      title: 'Bank Collection',
      icon: <BarChartOutlined className="bank-icon-blue" />,
    },
    {
      key: 'system_collection',
      title: 'System Collection',
      icon: <DatabaseOutlined className="bank-icon-purple" />,
    },
    {
      key: 'difference',
      title: 'Difference',
      icon: <WarningTwoTone twoToneColor="#faad14" className="bank-icon-warning" />,
    },
  ];

  const handleOpen = () => {
    setIsUploadOpen(true);
    setFileList([]);
    setUploadLoading(false);
  };

  const handleClose = () => {
    setIsUploadOpen(false);
    setFileList([]);
    setUploadLoading(false);
  };

  const handleUpload = async () => {
    setUploadLoading(true);
    if (fileList.length === 0) {
      message.warning('Please select a file before uploading!');
      setUploadLoading(false);
      return;
    }

    const file = fileList[0].originFileObj;
    try {
      const data = await uploadFileToAPI(file);
      message.success(data.message || 'File uploaded successfully!');
      handleClose();
    } catch (error) {
      message.error(error.message || 'Something went wrong!');
    } finally {
      setUploadLoading(false);
      get_bank_summary();
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
  };

  const uploadFileToAPI = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${getLocationOrigin()}/finance/upload_bank_transactions_from_excel/`;
    const options = {
      method: 'POST',
      body: formData,
    };

    const response = await fetch(url, options);
    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  };

  const columns = [
    {
      title: 'Month',
      dataIndex: 'month',
    },
    {
      title: 'Bank Collection',
      dataIndex: 'bank_amount',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'System Collection',
      dataIndex: 'collection_amount',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Difference',
      dataIndex: 'difference',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: '',
      width: 20,
      align: 'center',
      render: (_, record) => {
        const month = moment(record.month).format('MM');
        const year = moment(record.month).format('YYYY');

        return (
          <Link to={`/bank-collection/?month=${month}&year=${year}`}>
            <ArrowRightOutlined className="bank-icon-arrow" />
          </Link>
        );
      },
    },
  ];

  const summaryCells = [
    { key: 'bank_amount' },
    { key: 'system_collection' },
    { key: 'difference' },
  ];

  const renderSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <b>Total</b>
      </Table.Summary.Cell>

      {summaryCells.map((item, i) => (
        <Table.Summary.Cell key={item.key} index={i + 2} align="right">
          <b>{formatNumber(summaryTotals[item.key])}</b>
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  const renderRightSideActions = () => (
    <>
      <Button
        type="primary"
        icon={<UploadOutlined />}
        className="bank-upload-btn"
        onClick={handleOpen}
      >
        Upload Bank Transaction List
      </Button>
      <Modal
        title="Upload Bank Payment List"
        open={isUploadOpen}
        onCancel={handleClose}
        confirmLoading={uploadLoading}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={uploadLoading}
            disabled={!fileList.length}
          >
            Upload
          </Button>,
        ]}
      >
        <Space direction="vertical" className="bank-space-full">
          <Upload
            accept=".xlsx"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Select .xlsx File</Button>
          </Upload>
        </Space>
      </Modal>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
    </>
  );

  return (
    <SummaryLayout
      title="Bank Summary"
      headerContent={renderRightSideActions()}
      statsConfig={statsConfig}
      totals={summaryTotals}
      statsLoading={summaryLoader}
      formatter={formatNumber}
      tableProps={{
        columns,
        data: summaryData,
        loading: summaryLoader,
        pagination: false,
      }}
      summary={summaryData.length ? renderSummary : undefined}
    />
  );
}

export default BankSummary;
